import { Trophy } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { VideoLoader } from '@/components/ui/video-loader';

import defaultConfig from '../config/celebration-modal-config.json';
import { useCelebrationModal } from '../hooks/useCelebrationModal';
import { CelebrationModalProps } from '../types';

/**
 * CelebrationModal Component
 *
 * Displays a congratulatory modal with animated confetti when users complete achievements.
 * Commonly used for completing cases, reaching milestones, or passing certifications.
 *
 * Features:
 * - Animated confetti effect on open (canvas-confetti)
 * - Trophy icon (customizable)
 * - Congratulatory message
 * - Auto-cleanup of animation on close
 * - Non-dismissible by default (prevents accidental closure)
 * - Fully customizable text and styling
 *
 * @example
 * ```tsx
 * <CelebrationModal
 *   isOpen={allCasesCompleted}
 *   onClose={() => setShowCelebration(false)}
 * />
 * ```
 */
export const CelebrationModal = ({
  isOpen,
  onClose,
  title = defaultConfig.defaults.title,
  message = defaultConfig.defaults.message,
  buttonText = defaultConfig.defaults.buttonText,
  showIcon = defaultConfig.defaults.showIcon,
  customIcon,
  allowDismiss = defaultConfig.defaults.allowDismiss,
  videoUrl,
  showSkipButton = true,
}: CelebrationModalProps) => {
  const [showContent, setShowContent] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useCelebrationModal(isOpen, defaultConfig);

  const handleSkipVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setShowContent(true);
  };

  // Reset showContent and loading state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (videoUrl) {
        // Si vidéo présente : afficher la vidéo d'abord
        setShowContent(false);
        setIsVideoLoading(true);
      } else {
        // Si pas de vidéo : afficher directement le contenu de célébration
        setShowContent(true);
      }
    }
  }, [isOpen, videoUrl]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-3xl [&>button]:hidden"
        onEscapeKeyDown={(e) => !allowDismiss && e.preventDefault()}
        onPointerDownOutside={(e) => !allowDismiss && e.preventDefault()}
        onInteractOutside={(e) => !allowDismiss && e.preventDefault()}
      >
        {/* Phase 1: Video with Skip button */}
        {videoUrl && !showContent && (
          <div className="relative aspect-video overflow-hidden rounded-lg bg-black">
            {/* Loader pendant le chargement */}
            {isVideoLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black">
                <VideoLoader size="large" />
              </div>
            )}

            <video
              ref={videoRef}
              src={videoUrl}
              className="h-full w-full object-contain"
              autoPlay
              playsInline
              onLoadedData={() => setIsVideoLoading(false)}
              onEnded={() => setShowContent(true)}
            />

            {/* Skip button overlay */}
            {showSkipButton && !isVideoLoading && (
              <Button
                onClick={handleSkipVideo}
                variant="ghost"
                className="absolute right-4 top-4 bg-black/50 text-white backdrop-blur-sm hover:bg-black/70"
              >
                SKIP
              </Button>
            )}
          </div>
        )}

        {/* Phase 2: Celebration content */}
        {showContent && (
          <>
            <DialogHeader>
              {showIcon && (
                <div
                  className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${defaultConfig.styles.iconBackgroundColor}`}
                >
                  {customIcon || (
                    <Trophy className={`h-10 w-10 ${defaultConfig.styles.iconColor}`} />
                  )}
                </div>
              )}
              <DialogTitle className="text-center text-2xl">{title}</DialogTitle>
              <DialogDescription className="text-center text-base">{message}</DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex justify-center">
              <Button onClick={onClose} className="px-8">
                {buttonText}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
